<!-- 

Displays a box with current files that were added to an entity and an upload button.
For a table display with more information use DocumentsTable

 -->
<template lang="pug">
.documents-box.relative.rounded-md(
  :style="{ maxWidth: props.maxWidth }",
  @drop="onFileDrop",
  @dragover.prevent,
  @dragenter="onDragEnter",
  @dragleave="onDragLeave"
)
  .flex.items-center.justify-between.mb-1
    .smx-label.mr-4 {{ $t("files") }}
    el-button(
      v-if="!disabled",
      type="primary",
      icon="Upload",
      text,
      :aria-label="$t('uploadDocument')",
      @click="openUploadDialog"
    ) {{ $t("uploadFiles") }}

  .files-list.bg-white.rounded-lg.p-4(v-loading="loading")
    el-alert(v-if="loadError", type="error", :closable="false") {{ $t("errorLoadingFiles") }}
    .file-grid.mb-4(v-else-if="loaded && documents.length > 0")
      template(v-for="file in documents", :key="file.uuid")
        a.mr-8.truncate.text-accent(
          :href="`/api/documents/${file.uuid}`",
          :title="file.name"
        ) {{ file.name }}
        span.truncate {{ file.description }}
        el-popconfirm(
          v-if="!disabled",
          :title="$t('confirmDelete')",
          :confirm-button-text="$t('delete')",
          :cancel-button-text="$t('cancel')",
          width="220",
          @confirm="deleteDocument(file.uuid)"
        )
          template(#reference)
            .pl-2.cursor-pointer.flex.justify-end
              el-icon
                Delete
        .placeholder(v-else)

    .empty(v-else-if="loaded")
      span.text-slate-400 {{ $t("noFiles") }}

  .absolute.inset-0.flex.items-center.justify-center.bg-slate-500.text-xl.text-white.pointer-events-none.rounded-md(
    :class="{ 'opacity-50': isDragOver, 'opacity-0': !isDragOver }"
  ) {{ $t("dropFilesHere") }}

el-dialog(
  v-model="uploadDialogVisible",
  :title="$t('uploadDocument')",
  width="90vw",
  :before-close="handleBeforeUploadDialogClose"
)
  .text-bold.text-slate-600(v-if="files.length === 0") {{ $t("addFilesDialogHelper") }}

  el-table.mt-4(v-if="files.length > 0", :data="files", table-layout="auto")
    el-table-column(:label="$t('file')", min-width="200")
      template(#default="{ row }")
        .flex.items-center.min-w-0
          span.truncate {{ row.name }}
          span.text-slate-400.shrink-0.ml-1.text-sm ({{ formatFileSize(row.size) }})

    el-table-column(
      v-if="!uploadStarted",
      :label="$t('description')",
      min-width="250"
    )
      template(#default="{ row }")
        el-input(
          :model-value="getFileDescription(getFileKey(row))",
          :disabled="uploadLoading",
          :aria-label="$t('description')",
          @update:model-value="updateFileDescription(row, $event)"
        )

    el-table-column(
      v-if="uploadStarted",
      :label="$t('upload')",
      min-width="200"
    )
      template(#default="{ row }")
        el-progress(
          v-if="uploadStatusMap.get(getFileKey(row)) === 'uploading'",
          :percentage="Number(uploadProgressMap.get(getFileKey(row)) ?? 0)"
        )
        .flex.items-center.gap-2.text-green-600(
          v-else-if="uploadStatusMap.get(getFileKey(row)) === 'success'"
        )
          el-icon
            CircleCheckFilled
        .flex.flex-row.items-center.gap-4(
          v-else-if="uploadStatusMap.get(getFileKey(row)) === 'failed'"
        )
          el-alert(
            type="error",
            :title="$t('uploadFailed')",
            :closable="false"
          )
          el-button(
            type="danger",
            size="small",
            :disabled="uploadLoading",
            @click="retryFile(row)"
          )
            el-icon
              RefreshRight
            | {{ $t("retry") }}

  .documents-file-drop.flex.text-slate-700.justify-center.items-center.text-xl.mt-4(
    :class="{ 'opacity-50': isUploadDragOver, 'opacity-100': !isUploadDragOver, 'p-16': files.length === 0, 'p-4': files.length > 0 }",
    :style="files.length > 0 ? 'height: 100px' : ''",
    @drop="onUploadFileDrop",
    @dragover="onUploadDragOver",
    @dragenter="onUploadDragEnter",
    @dragleave="onUploadDragLeave",
    @click="openFileDialog"
  ) {{ $t("dropFilesHere") }}
  input.documents-upload-input(
    ref="fileInput",
    type="file",
    hidden,
    accept="*",
    multiple,
    @change="onFileChange($event)"
  )

  template(#footer)
    el-button(@click="closeUploadDialog") {{ $t("cancel") }}
    el-button(
      v-if="!uploadStarted",
      type="primary",
      :disabled="uploadDisabled",
      @click="uploadDocuments"
    ) {{ $t("upload") }}

el-dialog(
  v-model="showEditDocumentDialog",
  :title="$t('editDocument')",
  :width="mq.mdPlus ? 500 : '100%'"
)
  smx-form
    smx-form-item(:label="$t('description')")
      el-input(v-model="description")
    template(#actions)
      .flex.items-end.w-full
      el-button(type="danger", @click="closeEditDocumentDialog") {{ $t("cancel") }}
      el-button(type="primary", @click="saveDescription") {{ $t("save") }}
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useMq } from "vue3-mq";
import { formatDateTime } from "@shared/util/formatting";
import {
  deleteDocument as deleteDocumentRequest,
  patchDocument,
  postDocument,
  searchDocuments,
  type DocumentsSearch,
  type FileMetaData,
} from "../../services/documents";
import type { AxiosProgressEvent } from "axios";
import axios from "@shared/services/axiosProvider";
import { ElMessageBox, ElNotification } from "element-plus";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  filter: DocumentsSearch;
  uploadProperty: Record<string, string>;
  disabled?: boolean;
  maxWidth?: number | string;
}>();

const mq = useMq();
const { t } = useI18n();

const documents = ref<FileMetaData[]>([]);
const loading = ref(false);
const loaded = ref(false);
const loadError = ref(false);

const isDragOver = ref(false);
const dragDepth = ref(0);

const paging = ref({ page: 1, pageSize: 30 });

onMounted(() => {
  validateProps();
  void loadDocuments();
});

async function loadDocuments() {
  loading.value = true;
  loadError.value = false;
  try {
    const response = await searchDocuments(paging.value, props.filter);
    documents.value = response.data.result;
  } catch {
    loadError.value = true;
  } finally {
    loading.value = false;
    loaded.value = true;
  }
}

function validateProps() {
  if (
    props.filter.entityId === undefined ||
    props.filter.entityType === undefined
  ) {
    throw new Error(
      "DocumentsBox: filter entityId and entityType are required",
    );
  }
}

watch(() => paging.value.page, loadDocuments);
watch(
  () => props.filter,
  () => {
    validateProps();
    void loadDocuments();
  },
  { deep: true },
);

function onFileDrop(event: DragEvent) {
  event.preventDefault();
  dragDepth.value = 0;
  isDragOver.value = false;
  if (!props.disabled && event.dataTransfer?.files.length) {
    openUploadDialog(event.dataTransfer.files);
  }
}

function onDragEnter(event: DragEvent) {
  event.preventDefault();
  dragDepth.value += 1;
  isDragOver.value = true;
}

function onDragLeave(event: DragEvent) {
  event.preventDefault();
  dragDepth.value = Math.max(0, dragDepth.value - 1);
  isDragOver.value = dragDepth.value > 0;
}

const uploadDialogVisible = ref(false);
const files = ref<File[]>([]);
const fileDescriptions = reactive(new Map<string, string>());
const uploadProgressMap = reactive(new Map<string, string>());
const uploadStatusMap = reactive(
  new Map<string, "uploading" | "success" | "failed">(),
);
const uploadStarted = ref(false);
const uploadAbortController = ref<AbortController | null>(null);
const isUploadDragOver = ref(false);
const uploadDragDepth = ref(0);
const fileInput = ref<HTMLInputElement | null>(null);

const uploadLoading = computed(() =>
  files.value.some(
    (file) => uploadStatusMap.get(getFileKey(file)) === "uploading",
  ),
);
const uploadDisabled = computed(
  () =>
    files.value.length === 0 ||
    uploadLoading.value ||
    files.value.some((file) => getFileDescription(getFileKey(file)) === ""),
);

function resetUploadDialog() {
  files.value = [];
  fileDescriptions.clear();
  uploadProgressMap.clear();
  uploadStatusMap.clear();
  uploadStarted.value = false;
  uploadAbortController.value = null;
  isUploadDragOver.value = false;
  uploadDragDepth.value = 0;
}

function openUploadDialog(filesToUpload?: FileList) {
  resetUploadDialog();
  uploadDialogVisible.value = true;
  if (filesToUpload) {
    addFiles(Array.from(filesToUpload));
  }
}

function getFileKey(file: Pick<File, "name" | "size">) {
  return `${file.name}::${file.size}`;
}

function getFileDescription(key: string) {
  return fileDescriptions.get(key) ?? "";
}

function updateFileDescription(file: File, value: string) {
  fileDescriptions.set(getFileKey(file), value);
}

function openFileDialog() {
  fileInput.value?.click();
}

function notifyDuplicateFiles(duplicateFiles: File[]) {
  const names = [...new Set(duplicateFiles.map((file) => file.name))].join(
    ", ",
  );
  ElNotification({
    type: "warning",
    message: t("documentsAlreadyInList", { names }),
  });
}

function addFiles(addedFiles: File[]) {
  const existingFileKeys = new Set(files.value.map((file) => getFileKey(file)));
  const seenAddedFileKeys = new Set<string>();
  const newFiles: File[] = [];
  const duplicateFiles: File[] = [];

  addedFiles.forEach((file) => {
    const key = getFileKey(file);
    if (existingFileKeys.has(key) || seenAddedFileKeys.has(key)) {
      duplicateFiles.push(file);
      return;
    }

    seenAddedFileKeys.add(key);
    newFiles.push(file);
    fileDescriptions.set(key, file.name);
  });

  files.value.push(...newFiles);

  if (duplicateFiles.length > 0) {
    notifyDuplicateFiles(duplicateFiles);
  }
}

function onUploadFileDrop(event: DragEvent) {
  event.preventDefault();
  uploadDragDepth.value = 0;
  isUploadDragOver.value = false;
  if (event.dataTransfer?.files.length) {
    addFiles(Array.from(event.dataTransfer.files));
  }
}

function onUploadDragEnter(event: DragEvent) {
  event.preventDefault();
  uploadDragDepth.value += 1;
  isUploadDragOver.value = true;
}

function onUploadDragLeave(event: DragEvent) {
  event.preventDefault();
  uploadDragDepth.value = Math.max(0, uploadDragDepth.value - 1);
  isUploadDragOver.value = uploadDragDepth.value > 0;
}

function onUploadDragOver(event: DragEvent) {
  event.preventDefault();
}

function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement;
  if (input.files) {
    addFiles(Array.from(input.files));
  }
  input.value = "";
}

function onUploadProgress(key: string, progressEvent: AxiosProgressEvent) {
  uploadProgressMap.set(key, (100 * (progressEvent.progress ?? 0)).toFixed(1));
}

async function uploadSingleFile(file: File, signal: AbortSignal) {
  const key = getFileKey(file);
  uploadStatusMap.set(key, "uploading");
  uploadProgressMap.set(key, "0");

  const formData = new FormData();
  formData.append("name", file.name);
  formData.append("description", getFileDescription(key));
  Object.entries(props.uploadProperty).forEach(([property, value]) => {
    formData.append(property, value);
  });
  formData.append("file", file, file.name);

  try {
    await postDocument(
      formData,
      (progressEvent) => onUploadProgress(key, progressEvent),
      signal,
    );
    uploadStatusMap.set(key, "success");
  } catch (error) {
    if (axios.isCancel(error)) {
      throw error;
    }
    uploadStatusMap.set(key, "failed");
    throw error;
  }
}

function allFilesUploaded() {
  return files.value.every(
    (file) => uploadStatusMap.get(getFileKey(file)) === "success",
  );
}

async function completeUpload() {
  if (!allFilesUploaded()) {
    return;
  }

  uploadDialogVisible.value = false;
  await loadDocuments();
}

async function uploadDocuments() {
  if (uploadDisabled.value) {
    return;
  }

  uploadStarted.value = true;
  const abortController = new AbortController();
  uploadAbortController.value = abortController;

  const results = await Promise.allSettled(
    files.value.map((file) => uploadSingleFile(file, abortController.signal)),
  );
  uploadAbortController.value = null;

  if (abortController.signal.aborted) {
    return;
  }

  if (results.some((result) => result.status === "rejected")) {
    ElNotification({
      type: "error",
      message: t("genericError"),
    });
    return;
  }

  await completeUpload();
}

async function retryFile(file: File) {
  const abortController = new AbortController();
  uploadAbortController.value = abortController;

  await uploadSingleFile(file, abortController.signal).catch((error) => {
    if (!axios.isCancel(error)) {
      ElNotification({
        type: "error",
        message: t("genericError"),
      });
    }
  });

  if (abortController.signal.aborted) {
    return;
  }

  uploadAbortController.value = null;

  if (allFilesUploaded()) {
    await completeUpload();
  }
}

async function canCloseUploadDialog() {
  if (uploadLoading.value) {
    try {
      await ElMessageBox.confirm(
        t("cancelUploadsConfirm"),
        t("cancelUploads"),
        {
          confirmButtonText: t("cancelUploadsYes"),
          cancelButtonText: t("cancelUploadsNo"),
          type: "warning",
        },
      );
    } catch {
      return false;
    }
  }

  uploadAbortController.value?.abort();
  uploadAbortController.value = null;
  return true;
}

async function handleBeforeUploadDialogClose(done: () => void) {
  if (await canCloseUploadDialog()) {
    done();
  }
}

async function closeUploadDialog() {
  if (await canCloseUploadDialog()) {
    uploadDialogVisible.value = false;
  }
}

function formatFileSize(bytes: number) {
  let size = bytes;
  let unitIndex = 0;

  while (size >= 1024 && unitIndex < 3) {
    size /= 1024;
    unitIndex++;
  }

  const unit =
    unitIndex === 0
      ? "B"
      : unitIndex === 1
        ? "KB"
        : unitIndex === 2
          ? "MB"
          : "GB";
  return `${size.toFixed(2)} ${unit}`;
}

function dateFormatter(row: FileMetaData) {
  return formatDateTime(row.modified);
}

function deleteDocument(id: string) {
  void deleteDocumentRequest(id).then(loadDocuments);
}

const showEditDocumentDialog = ref(false);

const description = ref("");

const editingDocument = ref<FileMetaData | null>(null);

function editDocument(document: FileMetaData) {
  showEditDocumentDialog.value = true;
  editingDocument.value = document;
  description.value = document.description;
}

function closeEditDocumentDialog() {
  description.value = "";
  editingDocument.value = null;
  showEditDocumentDialog.value = false;
}

function saveDescription() {
  if (editingDocument.value === null) {
    return;
  }
  void patchDocument(editingDocument.value.uuid, getDocumentEntity(), {
    description: description.value,
  }).then(loadDocuments);
  closeEditDocumentDialog();
}

function getDocumentEntity() {
  if (
    props.filter.entityId === undefined ||
    props.filter.entityType === undefined
  ) {
    throw new Error(
      "DocumentsBox: filter entityId and entityType are required",
    );
  }
  return {
    entityId: props.filter.entityId,
    entityType: props.filter.entityType,
  };
}
</script>

<style scoped>
.file-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 30px;
  gap: 1rem;
}

.documents-file-drop {
  border-radius: 8px;
  border-style: dashed;
  border-color: var(--el-border-color);
  border-width: 4px;
}
</style>
